import { createFileRoute } from '@tanstack/react-router';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { AuditDocumentRequestType } from '@ata-digital/client-tax-system-api-client';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';
import { ClientBinderView } from '@/features/audit-binders/client/ClientBinderView';
import { BinderDetail } from '@/features/audit-binders/detail/BinderDetail';
import { HEADER_HEIGHT, MOBILE_PAGE_HEIGHT } from '@/constants/layout';

interface BinderSearch {
  /** Mobile ClientBinderView tab selection. */
  tab?: 'requests' | 'messages' | 'files';
  /** Used by the desktop two-pane workspace to remember which item is open. */
  item?: string;
  // The desktop view renders the shared BinderDetail workspace, which keeps its
  // tab/filter/sort/type controls in the URL. These must be whitelisted here or
  // TanStack Router strips them and the controls silently no-op.
  view?: 'requests' | 'documents' | 'comments' | 'signatures';
  filter?: 'all' | 'needs-me' | 'open';
  sort?: 'priority' | 'due' | 'created';
  requestType?: AuditDocumentRequestType;
  /** Deep-link from a notification: open the signing dialog for this document. */
  sign?: string;
}

const requestTypeValues = Object.values(AuditDocumentRequestType) as string[];

export const Route = createFileRoute('/audit-binders/$binderId/')({
  validateSearch: (search): BinderSearch => ({
    tab: search.tab === 'messages' || search.tab === 'files' ? search.tab : undefined,
    item: typeof search.item === 'string' ? search.item : undefined,
    view:
      search.view === 'documents' || search.view === 'comments' || search.view === 'signatures'
        ? search.view
        : undefined,
    filter:
      search.filter === 'all' || search.filter === 'open' || search.filter === 'needs-me' ? search.filter : undefined,
    sort: search.sort === 'due' || search.sort === 'created' ? search.sort : undefined,
    requestType:
      typeof search.requestType === 'string' && requestTypeValues.includes(search.requestType)
        ? (search.requestType as AuditDocumentRequestType)
        : undefined,
    sign: typeof search.sign === 'string' ? search.sign : undefined,
  }),
  errorComponent: QueryErrorBoundary,
  component: ClientAuditBinderDetailPage,
});

function ClientAuditBinderDetailPage() {
  const { binderId } = Route.useParams();
  const theme = useTheme();
  // At md+ we have room for the two-pane workspace; below that the cards-first
  // mobile view is much more usable than a stacked workspace.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        // The desktop workspace keeps its own height; only the mobile view is sized to the padded layout.
        height: { ...MOBILE_PAGE_HEIGHT, md: `calc(100dvh - ${HEADER_HEIGHT.sm}px)` },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isDesktop ? <BinderDetail binderId={binderId} /> : <ClientBinderView binderId={binderId} />}
    </Box>
  );
}
